import React from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  EntityValidationContent,
  EntityValidationPage,
} from '@backstage-community/plugin-entity-validation';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  TechDocsEditPageAddon as techDocsEditPageAddonExtension,
  TechdocsEditorPage,
  techdocsEditorPlugin,
} from '@estehsaan/backstage-plugin-techdocs-editor';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, entityPresentationModule, searchApiModule } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  ErrorPage,
  IdentityProviders,
  OAuthRequestDialog,
  Progress,
  SignInProviderConfig,
} from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
} from '@backstage/core-compat-api';
import { AppRouter, FeatureFlagged, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import * as plugins from './plugins';
import {
  attachComponentData,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  DefaultFilters,
  catalogApiRef,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import { SignInPage } from '@backstage/core-components';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { CatalogPage } from './components/catalog/CatalogPage';
import {
  SystemLandscapePage,
  PlatformServicesPage,
  ServiceProvidersPage,
  TechDocsContentHeader,
  TechDocsPageWrapper,
  TeamsPage,
  ProductPickerExtension,
  SystemPickerExtension,
  TeamPickerExtension,
  IconProvider,
  ProviderPickerExtension,
  ClusterGroupPickerExtension,
  NamespacePickerExtension,
  PlatformTeamPicker,
  ServiceProvidersOnlyPicker,
  ArchivedPicker,
  VirtualPicker,
  CmdbIngestionContent,
  CatalogStatisticsContent,
  EntityGraphNode,
} from '@internal/plugin-rtl-data';
import { AnnouncementPage } from './components/news/NewsPage';
import { AnnouncementsPage } from '@procore-oss/backstage-plugin-announcements';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { UnprocessedEntitiesContent } from '@backstage/plugin-catalog-unprocessed-entities';
import { Typography } from '@material-ui/core';
import { EnvironmentPickerExtension } from '@internal/plugin-rtl-data/src/scaffolder/EnvironmentPicker';
import {
  MY_PROFILE_PATH,
  MyProfileRedirect,
} from '@internal/plugin-rtl-data/src/components/menu';

const ANNOTATION_CMDB_UID = 'schema.dp.netrtl.com/uid';

const LEGACY_UID_KINDS: Record<string, string> = {
  group: 'Group',
  product: 'Product',
};

// A bare CMDB uid, five lowercase characters, e.g. `acafo`.
const CMDB_UID = /^[a-z0-9]{5}$/;

/**
 * Teams and products are named after their CMDB vuid (`team-acafo`), they used
 * to be named after the bare uid (`acafo`). A link that still carries the uid -
 * a bookmark, a wiki page, a chat message - is sent on to the entity that
 * annotates it, so that those links keep working.
 */
const CatalogEntityPageWithLegacyUid = () => {
  const { namespace, kind, name } = useParams();
  const location = useLocation();
  const catalogApi = useApi(catalogApiRef);
  // Only a group or product whose name has the shape of a uid can be a legacy
  // link, everything else goes straight to the entity page without asking the
  // catalog first.
  const legacyKind = LEGACY_UID_KINDS[kind?.toLocaleLowerCase('en-US') ?? ''];
  const isLegacyCandidate = Boolean(legacyKind) && CMDB_UID.test(name ?? '');

  const { value: entityName, loading } = useAsync(async () => {
    if (!isLegacyCandidate) {
      return undefined;
    }
    const { items } = await catalogApi.getEntities({
      filter: {
        kind: legacyKind,
        [`metadata.annotations.${ANNOTATION_CMDB_UID}`]: name!,
      },
      fields: ['metadata.name'],
    });
    return items[0]?.metadata.name;
  }, [isLegacyCandidate, legacyKind, name]);

  if (isLegacyCandidate && loading) {
    return <Progress />;
  }
  if (entityName && entityName !== name) {
    // Everything behind the name is the tab the link points at, it is kept.
    const tab = location.pathname.split('/').slice(5).join('/');
    return (
      <Navigate
        replace
        to={`/catalog/${namespace}/${kind}/${entityName}${tab ? `/${tab}` : ''}${location.search}`}
      />
    );
  }
  return <CatalogEntityPage />;
};

// The app reads the route of a page off the component data of the element it
// finds on the route, and it does not look any further than that element. The
// wrapper therefore has to carry the data of the page it stands in for, or
// nothing in the app can link to an entity any more.
attachComponentData(
  CatalogEntityPageWithLegacyUid,
  'core.plugin',
  catalogPlugin,
);
attachComponentData(
  CatalogEntityPageWithLegacyUid,
  'core.mountPoint',
  entityRouteRef,
);

const microsoftProvider: SignInProviderConfig = {
  id: 'microsoft-auth-provider',
  title: 'Microsoft',
  message: 'Sign in using Microsoft',
  apiRef: microsoftAuthApiRef,
};

// Guest sits next to Microsoft on a developer machine so the app can be opened
// without an Azure app registration. It is backed by `auth.providers.guest` in
// app-config.yaml; the deployed environments set that to null, so guest never
// exists anywhere but locally.
const devProviders: IdentityProviders = ['guest', microsoftProvider];

// eslint-disable-next-line no-console
console.info(`NODE_ENV: ${process.env.NODE_ENV}`);

// The package exports this addon as a bare `Extension` instead of exposing it
// through its plugin the way `ReportIssue` and friends do. An unexposed
// extension is not a component, and `createReactExtension` only runs its
// `attachComponentData` calls inside `expose`, so rendering the export
// directly would neither type-check nor register the addon under
// `TECHDOCS_ADDONS_KEY` for `useTechDocsAddons` to find. Exposing it here is
// what the plugin should have done itself.
const TechDocsEditPageAddon = techdocsEditorPlugin.provide(
  techDocsEditPageAddonExtension,
);

const convertedOptionsModule = convertLegacyAppOptions({
  apis,
  plugins: Object.values(plugins),
  components: {
    // Two providers mean there is a choice to make, so development gets the
    // plural `providers` and the grid of sign-in cards it renders. Everywhere
    // else the *singular* `provider` is what picks `SingleSignInPage`, and
    // only that page reads `auto` - the plural form drops `auto` on the floor.
    // Together with `enableExperimentalRedirectFlow` in the app config, a
    // visitor without a session is then sent straight to Microsoft instead of
    // being shown a login page.
    SignInPage: props =>
      process.env.NODE_ENV === 'development' ? (
        <SignInPage {...props} providers={devProviders} />
      ) : (
        <SignInPage {...props} auto provider={microsoftProvider} />
      ),
  },
  featureFlags: [
    {
      name: 'c4-deployment-diagram',
      description: 'Enables the c4 deployment diagram',
      pluginId: 'rtl-data',
    },
    {
      // Work in progress: the Service Providers and Platform Services pages
      // and every way to them - sidebar, Catalog menu, the catalog filter.
      // Keeps its name, so that whoever switched it on keeps it on.
      name: 'platform-catalog',
      description:
        'Shows the Service Providers and Platform Services pages (work in progress)',
      pluginId: 'rtl-data',
    },
  ],
  icons: IconProvider,
});

/**
 * A page only those see who switched its feature flag on; everyone else gets
 * the 404 they would get if it did not exist.
 *
 * The flag goes around the page, not around its `Route`: the routes are
 * converted for the new frontend system, which takes nothing but `Route`
 * elements inside `FlatRoutes`.
 */
const FlaggedPage = (props: { flag: string; children: React.ReactNode }) => (
  <>
    <FeatureFlagged with={props.flag}>{props.children}</FeatureFlagged>
    <FeatureFlagged without={props.flag}>
      <ErrorPage status="404" statusMessage="PAGE NOT FOUND" />
    </FeatureFlagged>
  </>
);

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    {/* `CatalogIndexPage` stays the element - it is what the catalog's route
        ref is bound to - and renders its child in place of the stock page,
        see CatalogPage. */}
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CatalogPage
        filters={
          // The stock filters plus the platform team, which only products
          // carry - the picker leaves itself out for every other kind.
          <>
            {/* `ownerPickerMode` because the default one builds its options
                out of the `relations.ownedBy` facet, which holds entity refs
                and no titles, so every owner reads as a bare vuid. `all`
                queries the entities themselves and gets their names. */}
            <DefaultFilters
              initialKind="component"
              initiallySelectedFilter="owned"
              ownerPickerMode="all"
            />
            <PlatformTeamPicker />
            {/* Groups only: the teams that provide services to the
                platform, what the Catalog menu links to. */}
            <FeatureFlagged with="platform-catalog">
              <ServiceProvidersOnlyPicker />
            </FeatureFlagged>
            {/* Retired products and wound up teams are left out of the
                listing unless this is switched on. */}
            <ArchivedPicker />
            {/* Groups only: the teams and departments the CMDB calls
                virtual, which are listed and tagged like any other. */}
            <VirtualPicker />
          </>
        }
      />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPageWithLegacyUid />}
    >
      {entityPage}
    </Route>
    <Route
      path="/docs"
      element={
        <TechDocsIndexPage
          PageWrapper={TechDocsPageWrapper}
          CustomHeader={TechDocsContentHeader}
        />
      }
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        {/* Puts an "Edit this page" button in the reader subheader. It resolves
            its target with `useRouteRef(editorRouteRef)` and renders nothing at
            all when that ref is unbound, so the route below is what makes the
            button appear. */}
        <TechDocsEditPageAddon />
      </TechDocsAddons>
    </Route>
    {/* The editor page. Mounting it here is not only about reaching the page:
        `convertLegacyAppRoot` converts the plugin behind a legacy routable
        extension, and that conversion is what registers the plugin's
        `TechDocsEditorApiRef` factory. Without this route nothing provides the
        api and every editor surface - this page, the "Edit this page" button
        and the entity "Edit Docs" tab - dies with
        `No implementation available for apiRef{plugin.techdocs-editor.api}`.

        The plugin also ships an `/alpha` entry point that would provide the
        api, but new-frontend-system package discovery is gated on the
        `app.packages` config key, which this app does not set, so that entry
        point is never loaded. Do not additionally export `techdocsEditorPlugin`
        from `plugins.ts`: `convertLegacyAppOptions` would then offer the same
        api under the `app` plugin and startup fails with
        `API_FACTORY_CONFLICT`. */}
    <Route
      path="/techdocs-editor/:namespace/:kind/:name"
      element={<TechdocsEditorPage />}
    />
    <Route
      path="/create"
      element={
        <ScaffolderPage
          // Named the way the sidebar names it.
          headerOptions={{
            title: 'Scaffolder',
            pageTitleOverride: 'Scaffolder',
          }}
          groups={[
            {
              title: 'Services',
              filter: entity => entity?.spec?.type === 'service',
            },
            {
              title: 'Documentation',
              filter: entity => entity?.spec?.type === 'documentation',
            },
            {
              title: 'Frontend Examples',
              filter: entity =>
                (entity?.metadata?.tags?.includes('frontend') &&
                  entity?.spec?.type === 'example') ??
                false,
            },
            {
              title: 'Backend Examples',
              filter: entity =>
                (entity?.metadata?.tags?.includes('backend') &&
                  entity?.spec?.type === 'example') ??
                false,
            },
          ]}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <TeamPickerExtension />
        <SystemPickerExtension />
        <ProductPickerExtension />
        <ProviderPickerExtension />
        <ClusterGroupPickerExtension />
        <EnvironmentPickerExtension />
        <NamespacePickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    {/* The page used to live here, links to it are kept alive. It listed
        what the catalog lists under these filters. */}
    <Route
      path="/innersource"
      element={
        <Navigate
          replace
          to="/catalog?filters[kind]=component&filters[tags]=innersource&filters[user]=all"
        />
      }
    />
    <Route
      path="/service-providers"
      element={
        <FlaggedPage flag="platform-catalog">
          <ServiceProvidersPage />
        </FlaggedPage>
      }
    />
    <Route
      path="/platform-services"
      element={
        <FlaggedPage flag="platform-catalog">
          <PlatformServicesPage />
        </FlaggedPage>
      }
    />
    <Route path="/landscape" element={<SystemLandscapePage />} />
    <Route path="/organization" element={<TeamsPage />} />
    {/* The page used to live here, links to it are kept alive. */}
    <Route path="/teams" element={<Navigate replace to="/organization" />} />
    <Route path={MY_PROFILE_PATH} element={<MyProfileRedirect />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={2000} height={1000} />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route
      path="/catalog-graph"
      element={
        <CatalogGraphPage
          initialState={{ maxDepth: 1 }}
          renderNode={EntityGraphNode}
        />
      }
    />
    <Route path="/news" element={<AnnouncementPage />} />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    {/* `contents` hangs the tab off the stock DevTools page, so Info, Config
        and Scheduled Tasks keep coming from the plugin instead of being
        copied into a page of our own. */}
    <Route
      path="/devtools"
      element={
        <DevToolsPage
          contents={[
            {
              // What the catalog holds, counted - next to the tabs below,
              // which report what went wrong getting it there.
              path: 'catalog-statistics',
              title: 'Catalog Statistics',
              children: <CatalogStatisticsContent />,
            },
            {
              path: 'entity-validation',
              title: 'Entity Validation',
              children: (
                <EntityValidationContent
                  contentHead={
                    <Typography variant="h6">Entity Validation</Typography>
                  }
                />
              ),
            },
            {
              // Entities that reached the catalog and failed to process, or
              // are still waiting to be processed. The tab the plugin attaches
              // to the DevTools page by itself only lands on the new frontend
              // system's page, this one is the legacy component.
              path: 'unprocessed-entities',
              title: 'Unprocessed Entities',
              children: <UnprocessedEntitiesContent />,
            },
            {
              // The counterpart to the tab above: what the CMDB export loses
              // before the catalog is told about it at all.
              path: 'cmdb-ingestion',
              title: 'CMDB Ingestion',
              children: <CmdbIngestionContent />,
            },
          ]}
        />
      }
    />
    <Route path="/entity-validation" element={<EntityValidationPage />} />
  </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);

const app = createApp({
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },

  features: [
    convertedOptionsModule,
    entityPresentationModule,
    searchApiModule,
    ...convertedRootFeatures,
  ],
});

export default app.createRoot();
